import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@/redux/Store';
import api from '@/utils/api';
import { getMediaUrl } from '@/constants/media';
import { formatName } from '@/utils/formatName';
import { formatPrice } from '@/utils/formatPrice';
import { profileLink } from '@/utils/profileLink';
import './Leaderboards.scss';

type Board = 'most-reputable' | 'most-trending' | 'most-expensive' | 'most-purchased' | 'most-viewed' | 'most-followed';
type Scope = 'global' | 'home' | 'touring';

const SCOPES: { scope: Scope; label: string; hint: string }[] = [
  { scope: 'home',    label: 'Home',    hint: 'Users sharing your home location' },
  { scope: 'touring', label: 'Touring', hint: 'Users in or visiting your touring location' },
  { scope: 'global',  label: 'Global',  hint: 'Everyone on Klyx' },
];

interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  nftProfileId: number | null;
  value: number;
  metric: string;
  location: {
    city: string | null;
    admin1: string | null;
    countryCode: string | null;
  } | null;
}

interface ApiResponse {
  board: Board;
  scope: Scope;
  scopeRequested: Scope;
  availableScopes: Scope[];
  /** Country + state being filtered on. Null when scope is global. */
  scopeArea: { countryCode: string; admin1: string | null } | null;
  entries: LeaderboardEntry[];
  /** Caller's row when they're outside the top N (null if anonymous or in entries). */
  self: LeaderboardEntry | null;
}

const TABS: { board: Board; label: string; subtitle: string }[] = [
  { board: 'most-reputable', label: 'Most Reputable', subtitle: 'Highest KlyxValue overall' },
  { board: 'most-trending', label: 'Most Trending', subtitle: 'Fastest-rising right now' },
  { board: 'most-expensive', label: 'Most Expensive', subtitle: 'Highest listed prices' },
  { board: 'most-purchased', label: 'Most Trades', subtitle: 'Total buys and sells' },
  { board: 'most-viewed', label: 'Most Viewed', subtitle: 'Most profile visits' },
  { board: 'most-followed', label: 'Most Followed', subtitle: 'Largest follower count' },
];

// Leaderboard rows are compact — use a tighter cap than the platform default.
const LEADERBOARD_NAME_LIMIT = 20;

const formatValue = (entry: LeaderboardEntry): string => {
  switch (entry.metric) {
    case 'klv':
      // formatPrice handles the unit suffix + compact form for big values
      // (e.g. "6.95t KLV" instead of "6,945,036,440,091.14 KLV").
      return formatPrice(entry.value);
    case 'trades':
      return `${entry.value.toLocaleString()} trade${entry.value === 1 ? '' : 's'}`;
    case 'views':
      return `${entry.value.toLocaleString()} view${entry.value === 1 ? '' : 's'}`;
    case 'followers':
      return `${entry.value.toLocaleString()} follower${entry.value === 1 ? '' : 's'}`;
    case 'trending':
      // TrendingScore is dimensionless; 2 decimals reads as a score,
      // not a currency amount. No unit suffix.
      return entry.value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case 'reputation':
      // KlyxValue matches per-profile rep-card display: 2 decimals,
      // no unit suffix. Stays visually aligned with the on-profile
      // number so users don't have to reconcile two formats.
      return entry.value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    default:
      return entry.value.toLocaleString();
  }
};

// "City, State, US" style. Skips empty parts so users with just
// city + country (no admin1) render cleanly. Returns null when
// there's nothing to show.
const formatLocation = (loc: LeaderboardEntry['location']): string | null => {
  if (!loc) return null;
  const parts = [loc.city, loc.admin1, loc.countryCode].filter(
    (p): p is string => Boolean(p),
  );
  return parts.length > 0 ? parts.join(', ') : null;
};

const isValidScope = (s: string | null): s is Scope =>
  s === 'home' || s === 'touring' || s === 'global';

/**
 * One leaderboard row. Extracted because we render it both for the
 * normal list and for the caller's own row appended at the bottom
 * when they're outside the visible top N.
 */
const renderRow = (
  entry: LeaderboardEntry,
  currentUserId: string | undefined,
  activeBoard: Board,
) => (
  <li
    key={`${activeBoard}-${entry.userId}-${entry.rank}`}
    className={`leaderboards-row ${entry.userId === currentUserId ? 'is-me' : ''}`}
  >
    <div className={`leaderboards-rank rank-${Math.min(entry.rank, 4)}`}>
      #{entry.rank}
    </div>
    <Link
      to={profileLink(entry.username, 'leaderboard')}
      className="leaderboards-avatar"
      aria-label={`View ${entry.displayName}'s profile`}
    >
      {entry.avatarUrl ? (
        <img
          src={getMediaUrl(entry.avatarUrl) ?? undefined}
          alt={entry.displayName}
        />
      ) : (
        <div className="leaderboards-avatar-placeholder">
          {entry.displayName[0]?.toUpperCase() ?? '?'}
        </div>
      )}
    </Link>
    <div className="leaderboards-identity">
      <Link
        to={profileLink(entry.username, 'leaderboard')}
        className="leaderboards-name"
      >
        {formatName({ displayName: entry.displayName, username: entry.username, limit: LEADERBOARD_NAME_LIMIT })}
      </Link>
      {(() => {
        const loc = formatLocation(entry.location);
        return loc ? (
          <span className="leaderboards-location" title={loc}>{loc}</span>
        ) : null;
      })()}
    </div>
    <div className="leaderboards-value">{formatValue(entry)}</div>
  </li>
);

const LeaderboardsPage: React.FC = () => {
  const { board: boardFromUrl } = useParams<{ board?: Board }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useAppSelector((state) => state.auth?.user);

  const isValidBoard = (b: string | undefined): b is Board =>
    TABS.some((t) => t.board === b);
  // Default to "most-reputable" — KlyxValue is the durable
  // reputation signal (ADR-008), so it's the answer to the wider
  // question a first-time visitor is really asking ("who is worth
  // paying attention to on this platform?"). Trending is right next
  // to it as the second tab for the "who's popping right now" cut.
  // Bookmarks to specific boards still resolve.
  const activeBoard: Board = isValidBoard(boardFromUrl) ? boardFromUrl : 'most-reputable';

  // Scope persists in the URL so refreshing keeps the same view.
  // Default to 'global' on first visit — users explicitly opt into
  // a scoped view, so initial state can't briefly look like a stale
  // "you haven't set a home location" warning while data loads.
  const scopeParam = searchParams.get('scope');
  const requestedScope: Scope = isValidScope(scopeParam) ? scopeParam : 'global';

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [self, setSelf] = useState<LeaderboardEntry | null>(null);
  // Effective scope from the server — may differ from requested when
  // we asked for home/touring but the user hasn't set one. We show a
  // small notice when that happens.
  const [effectiveScope, setEffectiveScope] = useState<Scope>(requestedScope);
  // Scope buttons available to the caller: always includes 'global';
  // 'home'/'touring' only when the server confirms the caller has them
  // set. Defaults match the request so the segmented control doesn't
  // flicker mid-fetch.
  const [availableScopes, setAvailableScopes] = useState<Scope[]>(['global']);
  const [scopeArea, setScopeArea] = useState<ApiResponse['scopeArea']>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // Sync effectiveScope to requestedScope so the downgrade hint
        // can't briefly flash from a previous fetch's response while
        // a new request is in flight.
        setEffectiveScope(requestedScope);
        const { data } = await api.get<ApiResponse>(`/leaderboards/${activeBoard}`, {
          params: { limit: 50, scope: requestedScope },
        });
        if (!cancelled) {
          setEntries(data.entries);
          setEffectiveScope(data.scope);
          setSelf(data.self);
          setAvailableScopes(data.availableScopes ?? ['global']);
          setScopeArea(data.scopeArea ?? null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error ?? 'Failed to load leaderboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBoard, requestedScope]);

  const handleTabClick = (board: Board) => {
    // Preserve scope across board changes.
    const suffix = requestedScope === 'global' ? '' : `?scope=${requestedScope}`;
    navigate(`/leaderboards/${board}${suffix}`);
  };

  const handleScopeChange = (scope: Scope) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (scope === 'global') next.delete('scope');
        else next.set('scope', scope);
        return next;
      },
      { replace: true },
    );
  };

  const scopeDowngraded = requestedScope !== effectiveScope && requestedScope !== 'global';

  return (
    <div className="leaderboards-page">
      <div className="leaderboards-container">
        <header className="leaderboards-header">
          <h1>Leaderboards</h1>
          <p>See who's leading the platform across every dimension.</p>
        </header>

        {/* Location scope: home / touring / global. Each button only
            appears if the server says the caller can use it (i.e. the
            user has set that location). Global is always available. */}
        <div className="leaderboards-scope" role="tablist" aria-label="Scope">
          {SCOPES.filter((opt) => availableScopes.includes(opt.scope)).map((opt) => (
            <button
              key={opt.scope}
              type="button"
              role="tab"
              aria-selected={opt.scope === requestedScope}
              title={opt.hint}
              className={`leaderboards-scope__btn ${opt.scope === requestedScope ? 'active' : ''}`}
              onClick={() => handleScopeChange(opt.scope)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {/* Show the active scope area ("Showing: Massachusetts, US")
            so users can tell what's being filtered when not on Global.
            Cards still show city — only the filter widens. */}
        {!loading && effectiveScope !== 'global' && scopeArea && (
          <p className="leaderboards-hint leaderboards-hint--area">
            Showing: {[scopeArea.admin1, scopeArea.countryCode].filter(Boolean).join(', ')}
          </p>
        )}
        {/* Gate on !loading so the hint can't flash from a previous
            fetch's downgraded state while a new request is in flight. */}
        {!loading && scopeDowngraded && (
          <p className="leaderboards-hint leaderboards-hint--warn">
            You haven't set a {requestedScope} location yet — showing global results.
          </p>
        )}

        <div className="leaderboards-tabs" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.board}
              type="button"
              role="tab"
              aria-selected={tab.board === activeBoard}
              className={`leaderboards-tab ${tab.board === activeBoard ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.board)}
            >
              <span className="leaderboards-tab__label">{tab.label}</span>
              <span className="leaderboards-tab__subtitle">{tab.subtitle}</span>
            </button>
          ))}
        </div>

        <div className="leaderboards-body">
          {loading && <p className="leaderboards-hint">Loading…</p>}
          {!loading && error && (
            <p className="leaderboards-hint leaderboards-hint--error">{error}</p>
          )}
          {!loading && !error && entries.length === 0 && (
            <p className="leaderboards-hint">No entries yet — check back soon.</p>
          )}
          {!loading && !error && entries.length > 0 && (
            <ol className="leaderboards-list">
              {entries.map((entry) => renderRow(entry, currentUser?.id, activeBoard))}
            </ol>
          )}
          {/* Caller's own row appended at the bottom when they're outside
              the visible top N. Separator makes it clear this isn't
              continuous with the list above. */}
          {!loading && !error && self && (
            <>
              <div className="leaderboards-self-separator" aria-hidden="true">
                <span>·  ·  ·</span>
              </div>
              <ol className="leaderboards-list leaderboards-list--self">
                {renderRow(self, currentUser?.id, activeBoard)}
              </ol>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardsPage;
